<template>
  <div v-if="showComponent">
    <!--START: Vue Headful-->
    <vue-headful :title="meta.title" :description="meta.description" />
    <!--END: Vue Headful-->

    <div class="dashboard-wrapper">
      <!--START: Dashboard Header-->
      <DashboardHeader
        v-if="!isSso"
        @toggleCompany="toggleCompany"
        @showAdminSettings="showAdminSettings"
      ></DashboardHeader>
      <!--END: Dashboard Header-->

      <!--START: Router View-->
      <router-view
        id="dashboard-main"
        :key="$route.fullPath"
        @showAdminSettings="showAdminSettings"
      ></router-view>
      <!--END: Router View-->
    </div>

    <!-- Admin Settings -->
    <AdminSettings
      v-if="showOptions.adminSettings"
      :initialTab="settingsTab"
      @closeModal="closeModal"
    ></AdminSettings>
    <div
      class="bg-overlay"
      :class="{ show: showOptions.adminSettings }"
      @click="closeModal"
    ></div>

    <!-- Apps Promotion -->
    <AppsPromotion></AppsPromotion>

    <!--START: Loader-->
    <LineLoader :show="showOptions.lineLoader"></LineLoader>
    <!--END: Loader-->
  </div>
</template>

<script>
// Import components
import DashboardHeader from "@/components/dashboard/Header";
import LineLoader from "@/components/loaders/LineLoader";
import AdminSettings from "@/components/dashboard/AdminSettings";
import AppsPromotion from "@/components/dashboard/AppsPromotion";

// Importing services
import { AuthService } from "@/services";

export default {
  name: "Dashboard",
  data() {
    return {
      showComponent: false,
      showOptions: {
        lineLoader: false,
        taskList: false,
        adminSettings: false,
      },
      settingsTab: "user-access",
      meta: {
        title: "Dashboard - Macha",
        description: "",
      },
    };
  },

  components: {
    DashboardHeader,
    LineLoader,
    AdminSettings,
    AppsPromotion,
  },

  computed: {
    user() {
      return this.$store.getters.user.user;
    },
    company() {
      return this.$store.getters.company;
    },
    selectedApp() {
      return this.$store.getters.selectedApp;
    },
    isSso() {
      return this.$store.getters.isSso;
    },
  },

  watch: {
    $route(to) {
      if (!to.meta?.authRoute) this.checkAccess();
      if (to.name === "Logout") this.logoutUser();
    },
  },

  async created() {
    await this.checkAccess();
  },

  methods: {
    // Check if dashboard access can be granted
    async checkAccess() {
      // Check to see if the staff is authenticated
      if (await this.authenticateUser()) {
        if (!this.isZendeskConnected()) {
          this.$store.commit("updateRedirectRoute", "/zendesk/connection");
        }

        this.showComponent = true;
        this.checkIntegrationCallback();
        this.checkRedirectRoute();
        this.loadZendeskWidget(this.user);
      } else {
        this.showComponent = false;
        this.redirectSignin();
      }
    },

    // Check if the user has to be redirected anywhere
    checkRedirectRoute() {
      const redirectRoute = this.$store.getters.redirectRoute;
      this.$store.commit("updateRedirectRoute", "");

      if (redirectRoute) this.$router.push({ path: `${redirectRoute}` });
    },

    // Toggle the company
    async toggleCompany(data) {
      this.showOptions.lineLoader = true;
      await AuthService.ToggleCompany(data);

      this.$router.go();
    },

    // Check if returning from an OAuth integration callback
    checkIntegrationCallback() {
      const query = this.$route.query;

      if (query.integration_connected || query.integration_error) {
        this.settingsTab = "integrations";
        this.showOptions.adminSettings = true;

        // Clean query params from the URL
        const cleanQuery = { ...query };
        delete cleanQuery.integration_connected;
        delete cleanQuery.integration_error;
        delete cleanQuery.provider;
        delete cleanQuery.reason;
        this.$router.replace({ query: cleanQuery });
      }
    },

    // Admin settings modal
    showAdminSettings(tab) {
      this.settingsTab = tab || "user-access";
      this.showOptions.adminSettings = true;
    },

    // Load the Zendesk widget
    loadZendeskWidget(user) {
      if (document.getElementById("ze-snippet")) return;

      const script = document.createElement("script");
      script.id = "ze-snippet";
      script.src =
        "https://static.zdassets.com/ekr/snippet.js?key=f06bf424-e247-4b4f-924e-4333df3cf599";
      script.async = true;
      script.onload = () => {
        if (window.zE) {
          window.zE("webWidget", "identify", {
            name: user.email,
            email: user.email,
          });
        }
      };
      document.body.appendChild(script);
    },

    async closeModal() {
      this.showOptions.taskList = false;
      this.showOptions.adminSettings = false;
    },
  },
};
</script>

<style scoped lang="scss">
@import "@/assets/styles/shared/dashboard.scss";
.dashboard-wrapper {
  display: flex;
  flex-direction: column;
  height: 100vh;
}

#dashboard-main {
  flex: 1 1 auto;
  display: flex;
  overflow: hidden;
  min-height: 0;
}

::v-deep {
  .line-loader {
    position: fixed;
    bottom: 0;
  }
}
</style>
